import React, { useState, useRef, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { useDateFormat, formatShortDate } from '../settings/TimePicker';
import { AnimatePresence, motion } from 'framer-motion';
import { useSupabaseEvents, CalendarEvent } from '../../hooks/useSupabaseEvents';
import { useAuth } from '../../contexts/AuthContext';
import { useSpace } from '../../contexts/SpaceContext';
import { useCalendars } from '../../hooks/useCalendars';
import { useTheme } from '../../contexts/ThemeContext';
import { useInteraction } from '../../contexts/InteractionContext';
import { useImportedCalendars } from '../../hooks/useImportedCalendars';

// Layout components
import { CalendarHeader } from './layout/CalendarHeader';
import { CalendarSidebar } from './layout/CalendarSidebar';
import { MobileBottomBar } from './layout/MobileBottomBar';
import { MobileDrawer } from './layout/MobileDrawer';

// Event components
import { EventFormModal } from './events/EventFormModal';
import { EventTooltip } from './events/EventTooltip';
import { MobileEventSheet } from './events/MobileEventSheet';
import { ImportedEventDetail } from './events/ImportedEventDetail';

// Hooks
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import { useGoogleCalendarSync, googleCalIdFromUrl, isGoogleCalendarUrl } from '../../hooks/useGoogleCalendarSync';

// Dialog components
import DeleteRecurringDialog from './dialogs/DeleteRecurringDialog';
import EditRecurringDialog from './dialogs/EditRecurringDialog';
import AddCalendarModal from './dialogs/AddCalendarModal';
import { TagManagerModal } from './dialogs/TagManagerModal';
import { SettingsModal } from './dialogs/SettingsModal';
import { PrintDialog } from './dialogs/PrintDialog';

// View components (lazy-loaded for code splitting)
const CalendarGrid = lazy(() => import('./views/CalendarGrid').then(m => ({ default: m.CalendarGrid })));
const WeekView = lazy(() => import('./views/WeekView').then(m => ({ default: m.WeekView })));
const ScheduleView = lazy(() => import('./views/ScheduleView').then(m => ({ default: m.ScheduleView })));
const BoardView = lazy(() => import('./views/BoardView').then(m => ({ default: m.BoardView })));
const VerticalYearView = lazy(() => import('./views/VerticalYearView').then(m => ({ default: m.VerticalYearView })));

// Kanban
import { useSupabaseKanban, Todo } from '../../hooks/useSupabaseKanban';
import { useTaskSettings } from '../../hooks/useTaskSettings';
import { TodoFormModal } from './dialogs/TodoFormModal';
import { todosToCalendarEvents, isTodoEvent } from '../../utils/calendar/todoHelpers';

// Settings components
import SettingsPage from '../settings/SettingsPage';

// UI components
import CalendarQuickMenu from '../ui/CalendarQuickMenu';
import CategoryQuickMenu from '../ui/CategoryQuickMenu';

// Hooks
import { useCalendarState } from '../../hooks/calendar/useCalendarState';
import { useEventHandlers } from '../../hooks/calendar/useEventHandlers';
import { CalendarProvider } from '../../contexts/CalendarContext';

// Utilities
import { getRgbaColor, MONTHS } from '../../utils/calendar/constants';
import { notifySpaceMembers } from '../../utils/notifications/triggers';
import { useToast } from '../../contexts/ToastContext';
import { getDateKey } from '../../utils/calendar/dateHelpers';
import { filterEventsByTags, filterEventsBySearch, getUnassignedEvents, resolveCalendar, UNASSIGNED_CALENDAR_ID } from '../../utils/calendar/eventHelpers';
import { DraftCard } from './DraftCard';
import { loadEventDraft, loadTaskDraft } from '../../utils/formDraft';
import { useTags, Tag as ContentTag } from '../../hooks/useTags';
import { GlobalSearch } from './GlobalSearch';

export default function ModernAnnualCalendar() {
  const { user, signOut, signInWithGoogle } = useAuth();
  const { currentSpace, isPersonalSpace, filterByMember, spaces, setCurrentSpace, canEdit } = useSpace();
  const { theme, toggleTheme } = useTheme();
  const { isTouchDevice } = useInteraction();
  const { toast } = useToast();
  const dateFormat = useDateFormat();


  // Viewers can look but not create. Explain on interaction instead of letting
  // the click through to an insert the database will reject.
  const readOnlyNotice = useCallback(() => {
    toast('You have view-only access to this space', 'info');
  }, [toast]);

  // Lock body scroll while calendar is mounted (prevents mobile bounce)
  useEffect(() => {
    document.documentElement.classList.add('no-scroll');
    return () => document.documentElement.classList.remove('no-scroll');
  }, []);

  // Global search shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Get the spaceId for filtering (null = personal, string = shared space)
  const spaceId = isPersonalSpace ? null : currentSpace?.id;
  const isViewer = !isPersonalSpace && currentSpace?.role === 'viewer';

  // Data hooks
  const { calendars, calendarsSpaceId, loading: calendarsLoading, addCalendar, updateCalendar, deleteCalendar, reorderCalendars } = useCalendars({ spaceId });
  const {
    events,
    loading,
    addEvent: saveEvent,
    addEventBatch,
    updateEvent: modifyEvent,
    deleteEvent: removeEvent,
    deleteEventBatch,
    refetch
  } = useSupabaseEvents({ spaceId });

  const {
    calendars: importedCalendars,
    events: importedEvents,
    loading: importedCalendarsLoading,
    syncing,
    addCalendar: addImportedCalendar,
    updateCalendar: updateImportedCalendar,
    deleteCalendar: deleteImportedCalendar,
    toggleCalendar,
    syncCalendar,
    importFromFile,
    refetch: refetchCalendars,
    refetchEvents: refetchImportedEvents,
    deleteImportedEvent,
    updateImportedEvent,
    addOptimisticEvent,
    updateOptimisticEvent,
  } = useImportedCalendars();

  // Google Calendar 2-way sync — stores pulled events in imported_events
  const gcalSync = useGoogleCalendarSync(refetchCalendars, refetchImportedEvents, {
    addEvent: addOptimisticEvent,
    updateEvent: updateOptimisticEvent,
  });

  // Wrap syncCalendar to route Google Calendar entries through gcalSync
  const handleSyncCalendar = useCallback(async (id: string) => {
    try {
      const cal = importedCalendars.find(c => c.id === id);
      if (isGoogleCalendarUrl(cal?.url)) {
        await gcalSync.pullChanges();
      } else {
        await syncCalendar(id);
      }
    } catch (e) {
      toast('Calendar sync failed', 'error');
    }
  }, [importedCalendars, syncCalendar, gcalSync, toast]);

  // Combine syncing states: ICS syncing (string|null) + Google syncing (boolean → calendar id)
  const combinedSyncing = syncing
    || (gcalSync.syncing
      ? importedCalendars.find(c => c.url === 'google-calendar-sync' || c.url.startsWith('google-calendar:'))?.id ?? null
      : null);

  // Open settings to the right section if redirected back from OAuth (e.g. Google Calendar connect)
  const [settingsInitialSection, setSettingsInitialSection] = useState<'calendar' | undefined>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('return') === 'settings-gcal' ? 'calendar' : undefined;
  });
  useEffect(() => {
    if (settingsInitialSection === 'calendar') {
      calendarState.setShowSettingsPage(true);
      if (new URLSearchParams(window.location.search).get('gcal-error') === '1') {
        toast("Couldn't connect Google Calendar — please try again", 'error');
      }
      window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pull Google Calendar changes on app load (once data is ready)
  const gcalPulledRef = useRef(false);
  useEffect(() => {
    if (gcalPulledRef.current || !gcalSync.syncEnabled || loading) return;
    gcalPulledRef.current = true;
    gcalSync.pullChanges().catch(() => {}); // best-effort on load; failures recorded per-calendar
  }, [gcalSync.syncEnabled, loading]);

  // Track if initial data has been loaded - used to show loading only on first load
  const hasInitialDataRef = useRef(false);
  useEffect(() => {
    if (!loading && !calendarsLoading && calendars.length >= 0) {
      hasInitialDataRef.current = true;
    }
  }, [loading, calendarsLoading, calendars]);

  // Task settings (auto-archive)
  const { settings: taskSettings, updateSettings: updateTaskSettings } = useTaskSettings({ spaceId });

  // Board view settings panel ref (wired to CalendarHeader button)
  const boardSettingsRef = useRef<(() => void) | null>(null);
  const [taskSettingsHasChanges, setTaskSettingsHasChanges] = useState(false);

  // Schedule view settings panel ref (wired to CalendarHeader button)
  const scheduleSettingsRef = useRef<(() => void) | null>(null);
  const [scheduleSettingsHasChanges, setScheduleSettingsHasChanges] = useState(false);

  // Kanban/Todo hook
  const {
    columns: kanbanColumns,
    todos,
    loading: kanbanLoading,
    addColumn,
    updateColumn,
    deleteColumn,
    reorderColumns,
    addTodo,
    updateTodo,
    deleteTodo,
    moveTodo,
    archiveTodo,
    unarchiveTodo,
    toggleTodoComplete,
    archiveAllTodosInColumn,
  } = useSupabaseKanban({
    spaceId,
    autoArchive: {
      enabled: taskSettings.auto_archive_enabled,
      days: taskSettings.auto_archive_days,
      enabledAt: taskSettings.auto_archive_enabled_at,
    },
  });

  // Content tags (text labels, per-space, multiple per event/todo)
  const spaceTags = useTags({ spaceId });
  const [selectedContentTags, setSelectedContentTags] = useState<ContentTag[]>([]);
  const [selectedTodoContentTags, setSelectedTodoContentTags] = useState<ContentTag[]>([]);

  // Map of todoId → ContentTag[] for displaying tags on task cards
  const [todoContentTagsMap, setTodoContentTagsMap] = useState<Map<string, ContentTag[]>>(new Map());
  // Tracks in-flight optimistic tag updates so the todos useEffect re-fetch doesn't overwrite them
  const pendingTagUpdatesRef = useRef<Map<string, ContentTag[]>>(new Map());

  // Map of eventId → ContentTag[] for displaying tags on schedule view cards
  const [eventContentTagsMap, setEventContentTagsMap] = useState<Map<string | number, ContentTag[]>>(new Map());

  // Active content tag filter — single tag, persists across views, resets on space switch
  const [activeContentTagFilter, setActiveContentTagFilter] = useState<ContentTag | null>(null);
  const [tagFilteredEventIds, setTagFilteredEventIds] = useState<Set<string> | null>(null);
  const [tagFilteredTodoIds, setTagFilteredTodoIds] = useState<Set<string> | null>(null);

  // Reset tag filter when space changes
  useEffect(() => {
    setActiveContentTagFilter(null);
    setTagFilteredEventIds(null);
    setTagFilteredTodoIds(null);
    setTodoContentTagsMap(new Map());
    setEventContentTagsMap(new Map());
  }, [spaceId]);

  // Batch-fetch content tags for all todos so cards can display them.
  // Pending optimistic updates are preserved so in-flight tag saves don't get overwritten.
  useEffect(() => {
    if (todos.length === 0) { pendingTagUpdatesRef.current.clear(); setTodoContentTagsMap(new Map()); return; }
    spaceTags.fetchTodoTags(todos.map(t => t.id)).then(fetched => {
      setTodoContentTagsMap(() => {
        pendingTagUpdatesRef.current.forEach((tags, id) => fetched.set(id, tags));
        return fetched;
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todos]);

  // Batch-fetch content tags for all native events so schedule view can display them
  useEffect(() => {
    if (events.length === 0) { setEventContentTagsMap(new Map()); return; }
    spaceTags.fetchEventTags(events.map(e => e.id)).then(rawMap => {
      const out = new Map<string | number, ContentTag[]>();
      rawMap.forEach((tags, key) => out.set(key, tags));
      setEventContentTagsMap(out);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // Fetch event/todo IDs for active content tag filter
  useEffect(() => {
    if (activeContentTagFilter) {
      // Set to empty sets immediately — prevents stale/unfiltered results during async load
      setTagFilteredEventIds(new Set<string>());
      setTagFilteredTodoIds(new Set<string>());
      spaceTags.fetchEventIdsByTag(activeContentTagFilter.id).then(setTagFilteredEventIds);
      spaceTags.fetchTodoIdsByTag(activeContentTagFilter.id).then(setTagFilteredTodoIds);
    } else {
      setTagFilteredEventIds(null);
      setTagFilteredTodoIds(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeContentTagFilter?.id]);

  // Todo form state
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [showTodoForm, setShowTodoForm] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [defaultColumnId, setDefaultColumnId] = useState<string | undefined>(undefined);
  const [defaultTodoDueDate, setDefaultTodoDueDate] = useState<string | undefined>(undefined);
  const [defaultTodoTitle, setDefaultTodoTitle] = useState<string | undefined>(undefined);


  // Show/hide todos on calendar views (persisted)
  const [showTodosOnCalendar, setShowTodosOnCalendar] = useState(() =>
    localStorage.getItem('calendar-show-todos') !== 'false'
  );

  // Print dialog state
  const [showPrintDialog, setShowPrintDialog] = useState(false);

  // Global search (Cmd/Ctrl+K)
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Get space members for assignee dropdown
  const { members } = useSpace();

  // Calendar state hook
  const calendarState = useCalendarState({
    calendars,
    calendarsSpaceId,
    isTouchDevice,
    spaceId,
  });

  // Open new event form (from global search — uses today as default date)
  const handleSearchNewEvent = useCallback((title?: string) => {
    const today = new Date();
    const dateKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
    calendarState.openNewEventForm({ dateKey, position: { x: window.innerWidth / 2, y: window.innerHeight / 2, centerHorizontally: true }, title });
  }, [calendarState]);

  // Open new task form (from global search)
  const handleSearchNewTask = useCallback((title?: string) => {
    setEditingTodo(null);
    setDefaultColumnId(kanbanColumns.find(c => !c.is_done_column)?.id);
    setDefaultTodoTitle(title);
    const t = new Date();
    setDefaultTodoDueDate(`${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`);
    setTodoFormPosition(null);
    setShowTodoForm(true);
  }, [kanbanColumns]);

  // Navigate to a specific date from global search
  const handleSearchNavigateToDate = useCallback((date: Date, view?: 'year' | 'month' | 'week' | 'schedule' | 'todo') => {
    calendarState.setCurrentYear(date.getFullYear());
    calendarState.setCurrentMonth(date.getMonth());
    // Set week start to Monday of the week containing this date
    const weekStart = new Date(date);
    const day = weekStart.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    weekStart.setDate(weekStart.getDate() + diff);
    weekStart.setHours(0, 0, 0, 0);
    calendarState.setCurrentWeekStart(weekStart);
    if (view) calendarState.setViewMode(view);
  }, [calendarState]);

  // Zoom toggle — CSS transitions on row min-height / flex-basis handle the animation
  const handleYearZoomToggle = useCallback(() => {
    calendarState.setYearZoom(!calendarState.yearZoom);
  }, [calendarState]);

  // Year layout toggle — horizontal (months as rows) vs vertical (months as columns)
  // On mobile, orientation drives layout; on desktop, user preference is persisted
  const isMobile = isTouchDevice;
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(() =>
    window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
  );

  useEffect(() => {
    if (!isMobile) return;
    const mql = window.matchMedia('(orientation: landscape)');
    const handler = (e: MediaQueryListEvent) => setOrientation(e.matches ? 'landscape' : 'portrait');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [isMobile]);

  const [yearLayoutPref, setYearLayoutPref] = useState<'horizontal' | 'vertical'>(() =>
    (localStorage.getItem('calendar-year-layout') as 'horizontal' | 'vertical') || 'horizontal'
  );
  const yearLayout = isMobile
    ? (orientation === 'landscape' ? 'horizontal' : 'vertical')
    : yearLayoutPref;

  const handleYearLayoutToggle = useCallback(() => {
    setYearLayoutPref(prev => {
      const next = prev === 'horizontal' ? 'vertical' : 'horizontal';
      localStorage.setItem('calendar-year-layout', next);
      return next;
    });
  }, []);

  // Print title based on current view
  const printTitle = useMemo(() => {
    const { viewMode, currentYear, currentMonth, currentWeekStart } = calendarState;
    if (viewMode === 'year') return `${currentYear}`;
    if (viewMode === 'month') return `${MONTHS[currentMonth]} ${currentYear}`;
    if (viewMode === 'week') {
      const iso = `${currentWeekStart.getFullYear()}-${String(currentWeekStart.getMonth()+1).padStart(2,'0')}-${String(currentWeekStart.getDate()).padStart(2,'0')}`;
      return `Week of ${formatShortDate(iso, dateFormat)} ${currentWeekStart.getFullYear()}`;
    }
    if (viewMode === 'schedule') return `Schedule ${currentYear}`;
    return '';
  }, [calendarState, dateFormat]);

  // Event handlers hook
  const eventHandlers = useEventHandlers({
    saveEvent,
    addEventBatch,
    modifyEvent,
    removeEvent,
    deleteEventBatch,
    refetch,
    events,
  });

  // Google Calendar — track per-event assignment
  const [selectedGoogleCalendarId, setSelectedGoogleCalendarId] = useState<string | null>(null);

  // Read-only detail for URL-synced imported events and viewer-role space events
  const [importedDetailEvent, setImportedDetailEvent] = useState<CalendarEvent | null>(null);
  const [importedDetailPosition, setImportedDetailPosition] = useState<{ x: number; y: number; barRect?: DOMRect } | null>(null);
  const [importedDetailReadOnly, setImportedDetailReadOnly] = useState(false);

  // Quick menu state
  const [quickMenuCategory, setQuickMenuCategory] = useState<{
    id?: string;
    name: string;
    color: string;
    is_shared?: boolean;
    share_token?: string | null;
  } | null>(null);
  const [quickMenuCategoryAnchor, setQuickMenuCategoryAnchor] = useState<HTMLButtonElement | null>(null);
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [showAddCalendarModal, setShowAddCalendarModal] = useState(false);
  const [quickMenuCalendar, setQuickMenuCalendar] = useState<{
    id: string;
    name: string;
    url: string;
    color: string;
  } | null>(null);
  const [quickMenuAnchor, setQuickMenuAnchor] = useState<HTMLButtonElement | null>(null);

  // Check for unassigned events and auto-include them in filters when they appear
  // This ensures events remain visible after their tag is deleted
  const unassignedEvents = getUnassignedEvents(events, calendars);
  const hasUnassignedEvents = unassignedEvents.length > 0;

  useEffect(() => {
    // When unassigned events appear and are not yet in filters, add them
    // This happens when a tag is deleted - events become unassigned
    if (hasUnassignedEvents && !calendarState.selectedCalendarFilters.has(UNASSIGNED_CALENDAR_ID)) {
      // Only add if the user has some filters selected (not deselected all)
      if (calendarState.selectedCalendarFilters.size > 0) {
        const newFilters = new Set(calendarState.selectedCalendarFilters);
        newFilters.add(UNASSIGNED_CALENDAR_ID);
        calendarState.setSelectedCalendarFilters(newFilters);
      }
    }
  }, [hasUnassignedEvents]);

  // Filter native events based on selected tags, search query, member filter, and content tag filter
  let filteredNativeEvents = filterEventsBySearch(
    filterEventsByTags(events, calendarState.selectedCalendarFilters, calendars),
    calendarState.searchQuery
  );

  // Apply content tag filter (text labels, separate from calendar tags)
  if (tagFilteredEventIds !== null) {
    filteredNativeEvents = filteredNativeEvents.filter(e => tagFilteredEventIds.has(String(e.id)));
  }

  // Apply member filter if in a shared space
  if (filterByMember && !isPersonalSpace) {
    filteredNativeEvents = filteredNativeEvents.filter(
      event => event.created_by === filterByMember
    );
  }

  // Convert imported events to CalendarEvent format
  const convertedImportedEvents: CalendarEvent[] = importedEvents.map(ie => {
    const calendar = importedCalendars.find(c => c.id === ie.calendar_id);
    return {
      id: ie.id,
      title: ie.title,
      notes: ie.description || '',
      dates: ie.dates,
      color: {
        bg: calendar?.color || 'bg-gray-500',
        gradient: calendar?.color || 'bg-gray-500',
        name: calendar?.name || 'Imported'
      },
      all_day: ie.all_day,
      start_time: ie.start_time,
      end_time: ie.end_time,
      isImported: true,
      importedCalendarUrl: calendar?.url,
      externalUid: ie.external_uid,
    } as CalendarEvent & { isImported?: boolean; importedCalendarUrl?: string; externalUid?: string };
  });

  // Combine native and imported events
  // Convert todos with due dates to calendar events
  const todoCalendarEvents = todosToCalendarEvents(todos, kanbanColumns, calendars);

  const filteredEvents = [
    ...filteredNativeEvents,
    ...convertedImportedEvents.filter(e =>
      !calendarState.searchQuery || e.title.toLowerCase().includes(calendarState.searchQuery.toLowerCase())
    ),
    // Include todos on calendar views:
    // - Schedule view always receives them (its own "Show tasks" toggle controls visibility)
    // - Other views (month/week/year) respect the global showTodosOnCalendar toggle
    ...((calendarState.viewMode !== 'todo' && (calendarState.viewMode === 'schedule' || showTodosOnCalendar)) ? todoCalendarEvents.filter(e =>
      !calendarState.searchQuery || e.title.toLowerCase().includes(calendarState.searchQuery.toLowerCase())
    ) : []),
  ];

  // Identify tags with color_cells enabled (background calendars)
  const colorCellsTagIds = useMemo(() => {
    const ids = new Set<string>();
    for (const tag of calendars) {
      if (tag.color_cells) ids.add(tag.id);
    }
    return ids;
  }, [calendars]);

  // Build background color map: dateKey → { color, events[] }
  const backgroundColorMap = useMemo(() => {
    const map = new Map<string, { color: string; events: CalendarEvent[] }>();
    if (colorCellsTagIds.size === 0) return map;

    for (const event of filteredEvents) {
      const tag = resolveCalendar(event, calendars);
      if (!tag || !colorCellsTagIds.has(tag.id)) continue;

      const rgbaColor = getRgbaColor(tag.color);

      for (const dateStr of event.dates) {
        const existing = map.get(dateStr);
        if (existing) {
          existing.events.push(event);
        } else {
          map.set(dateStr, { color: rgbaColor, events: [event] });
        }
      }
    }
    return map;
  }, [filteredEvents, calendars, colorCellsTagIds]);

  // Events excluding background calendar events (for year/month grid views)
  const nonBackgroundEvents = useMemo(() => {
    if (colorCellsTagIds.size === 0) return filteredEvents;
    return filteredEvents.filter(event => {
      const tag = resolveCalendar(event, calendars);
      return !tag || !colorCellsTagIds.has(tag.id);
    });
  }, [filteredEvents, colorCellsTagIds, calendars]);

  // Mobile event preview sheet
  const [mobilePreviewEvent, setMobilePreviewEvent] = useState<CalendarEvent | null>(null);

  // Swipe navigation for mobile
  useSwipeNavigation({
    onSwipeLeft: calendarState.handleNavNext,
    onSwipeRight: calendarState.handleNavPrev,
    enabled: isTouchDevice && (calendarState.viewMode === 'month' || calendarState.viewMode === 'week'),
  });

  // Load existing content tags when opening an event for edit
  useEffect(() => {
    if (calendarState.editingEvent && !calendarState.editingEvent.isImported) {
      spaceTags.fetchEventTags([calendarState.editingEvent.id]).then(map => {
        setSelectedContentTags(map.get(String(calendarState.editingEvent!.id)) || []);
      });
    } else {
      setSelectedContentTags([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarState.editingEvent?.id]);

  // Load existing content tags when opening a todo for edit.
  // Use the already-loaded map first (instant), fall back to a fetch only if the todo isn't in the map yet.
  useEffect(() => {
    if (editingTodo) {
      const cached = todoContentTagsMap.get(editingTodo.id);
      if (cached !== undefined) {
        setSelectedTodoContentTags(cached);
      } else {
        spaceTags.fetchTodoTags([editingTodo.id]).then(map => {
          setSelectedTodoContentTags(map.get(String(editingTodo.id)) || []);
        });
      }
    } else {
      setSelectedTodoContentTags([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTodo?.id]);

  // Track where the event form was opened from
  const [eventFormSource, setEventFormSource] = useState<'header' | 'calendar' | 'board'>('calendar');

  // Handle adding event from header
  const handleAddEventFromHeader = (dateKey: string, position: { x: number; y: number }) => {
    if (!canEdit) { readOnlyNotice(); return; }
    setEventFormSource('header');
    calendarState.openNewEventForm({
      dateKey,
      position: { x: position.x, y: position.y, centerHorizontally: false },
    });
  };

  // Todo form position (for positioning near the "+" button)
  const [todoFormPosition, setTodoFormPosition] = useState<{ x: number; y: number } | null>(null);

  // Draft restore flags — set when opening a modal from the DraftCard
  const [openEventFromDraft, setOpenEventFromDraft] = useState(false);
  const [openTaskFromDraft, setOpenTaskFromDraft] = useState(false);

  // Edit recurring dialog
  const [showEditRecurringDialog, setShowEditRecurringDialog] = useState(false);
  const pendingRecurringEditRef = useRef<{ editingEvent: CalendarEvent; formData: any } | null>(null);

  // Handle adding todo from header "+" button
  const handleAddTodoFromHeader = (position: { x: number; y: number }) => {
    setEditingTodo(null);
    setDefaultColumnId(kanbanColumns.find(c => !c.is_done_column)?.id);
    const t = new Date();
    setDefaultTodoDueDate(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`);
    setTodoFormPosition(position);
    setShowTodoForm(true);
  };

  // Open event modal from DraftCard (restores saved draft)
  const handleOpenEventDraft = useCallback(() => {
    const draft = loadEventDraft();
    const d = new Date();
    const dateKey = draft?.startDate || getDateKey(d.getFullYear(), d.getMonth(), d.getDate());
    setOpenEventFromDraft(true);
    setEventFormSource('header');
    calendarState.openNewEventForm({
      dateKey,
      position: { x: window.innerWidth / 2, y: window.innerHeight / 3, centerHorizontally: true },
    });
  }, [calendarState]);

  // Open task modal from DraftCard (restores saved draft)
  const handleOpenTaskDraft = useCallback(() => {
    setOpenTaskFromDraft(true);
    setEditingTodo(null);
    setDefaultColumnId(kanbanColumns.find(c => !c.is_done_column)?.id);
    setDefaultTodoDueDate(undefined);
    setTodoFormPosition(null);
    setShowTodoForm(true);
  }, [kanbanColumns]);

  const buildFormData = () => ({
    title: calendarState.eventTitle,
    notes: calendarState.eventNotes,
    selectedCells: calendarState.selectedCells,
    startDate: calendarState.startDate,
    endDate: calendarState.endDate,
    selectedCalendar: calendarState.selectedCalendar,
    allDay: calendarState.allDay,
    fullSize: calendarState.fullSize,
    startTime: calendarState.startTime,
    endTime: calendarState.endTime,
    recurrenceRule: calendarState.recurrenceRule,
  });

  // Handle save event (with guard against rapid double-submit)
  const savingRef = useRef(false);
  const handleSaveEvent = async () => {
    if (savingRef.current) return;

    const formData = buildFormData();

    // If editing a recurring event, ask first
    if (calendarState.editingEvent && (calendarState.editingEvent.recurrence_rule || calendarState.editingEvent.parent_event_id)) {
      pendingRecurringEditRef.current = { editingEvent: calendarState.editingEvent, formData };
      setShowEditRecurringDialog(true);
      return;
    }

    savingRef.current = true;

    try {

      if (selectedGoogleCalendarId) {
        if (calendarState.editingEvent?.isImported) {
          // Update existing Google Calendar event
          const event = calendarState.editingEvent;
          const cells = formData.selectedCells.length > 0 ? [...formData.selectedCells].sort() : [...event.dates];
          const updates = {
            title: formData.title,
            description: formData.notes,
            dates: cells,
            all_day: formData.allDay,
            start_time: formData.allDay ? undefined : formData.startTime,
            end_time: formData.allDay ? undefined : formData.endTime,
          };
          updateImportedEvent(event.id, updates);
          // Push change to Google using the Google event ID directly, targeting the
          // event's own calendar (not the primary default).
          const externalUid = (event as any).externalUid;
          if (externalUid && !externalUid.startsWith('temp-')) {
            const targetCal = googleCalIdFromUrl((event as any).importedCalendarUrl || '') || undefined;
            gcalSync.pushEvent({ ...event, ...updates, notes: updates.description } as any, targetCal, externalUid);
          }
          setSelectedGoogleCalendarId(null);
          calendarState.resetForm();
        } else {
          // Create on Google Calendar
          const cells = formData.selectedCells;
          gcalSync.createGoogleEvent({
            title: formData.title,
            notes: formData.notes,
            dates: [...cells].sort(),
            all_day: formData.allDay,
            start_time: formData.allDay ? null : formData.startTime,
            end_time: formData.allDay ? null : formData.endTime,
          });
          // If switching an existing native event to Google Calendar, delete the native one
          if (calendarState.editingEvent) {
            eventHandlers.deleteEvent(calendarState.editingEvent.id);
          }
          setSelectedGoogleCalendarId(null);
          calendarState.resetForm();
        }
      } else if (calendarState.editingEvent) {
        // Snapshot tags before resetForm clears editingEvent (which resets selectedContentTags)
        const editingEvent = calendarState.editingEvent;
        const tagsToSave = [...selectedContentTags];
        calendarState.resetForm();
        const ok = await eventHandlers.updateEvent(editingEvent, formData);
        if (ok) {
          toast('Event saved', 'success');
          await spaceTags.setEventTags(editingEvent.id, tagsToSave.map(t => t.id));
          // Trigger notification for shared spaces
          if (currentSpace?.id && !isPersonalSpace) {
            await notifySpaceMembers({
              type: 'event_updated',
              spaceId: currentSpace.id,
              eventTitle: editingEvent.title,
              actorId: user!.id,
              actorName: user!.user_metadata?.display_name || user!.email || 'Someone',
            });
          }
        } else {
          toast('Failed to save event', 'error');
        }
      } else {
        // Snapshot tags before resetForm
        const tagsToSave = [...selectedContentTags];
        calendarState.resetForm();
        const result = await eventHandlers.addEvent(formData);
        if (result === false) {
          toast('Failed to create event', 'error');
        } else if (result) {
          const created = result;
          await spaceTags.setEventTags(created.id, tagsToSave.map(t => t.id));
          toast('Event created', 'success', {
            label: 'Undo',
            onClick: () => removeEvent(created.id),
          });
          // Trigger notification for shared spaces
          if (currentSpace?.id && !isPersonalSpace) {
            await notifySpaceMembers({
              type: 'event_created',
              spaceId: currentSpace.id,
              eventTitle: created.title,
              actorId: user!.id,
              actorName: user!.user_metadata?.display_name || user!.email || 'Someone',
            });
          }
        }
      }
    } finally {
      savingRef.current = false;
    }
  };

  // Handle delete from form — close dialog first, then delete optimistically
  const handleDeleteFromForm = async () => {
    if (!calendarState.editingEvent) return;
    const event = calendarState.editingEvent;

    if (event.recurrence_rule || event.parent_event_id) {
      calendarState.setEventToDelete(event);
      calendarState.setShowDeleteDialog(true);
      calendarState.setShowEventForm(false);
      return;
    }

    // Close dialog first, then delete
    setSelectedGoogleCalendarId(null);
    calendarState.resetForm();

    if (event.isImported) {
      deleteImportedEvent(event.id);
      // Also delete from Google Calendar if it's a Google Calendar event
      const externalUid = (event as any).externalUid;
      const importedUrl = (event as any).importedCalendarUrl;
      if (isGoogleCalendarUrl(importedUrl) && externalUid && !externalUid.startsWith('temp-')) {
        gcalSync.deleteGoogleEventByGoogleId(externalUid, googleCalIdFromUrl(importedUrl) || undefined);
      }
    } else {
      const snapshot = { ...event };
      eventHandlers.deleteEvent(event.id);
      toast('Event deleted', 'info', {
        label: 'Undo',
        onClick: () => saveEvent(snapshot),
      });
      // Trigger notification for shared spaces
      if (event.space_id && !isPersonalSpace) {
        await notifySpaceMembers({
          type: 'event_deleted',
          spaceId: event.space_id,
          eventTitle: event.title,
          actorId: user!.id,
          actorName: user!.user_metadata?.display_name || user!.email || 'Someone',
        });
      }
    }
  };

  // Handle event click (edit) — route todos to TodoFormModal
  // On mobile, show preview sheet first instead of opening edit form directly
  const handleEventClick = (event: CalendarEvent, clickPosition?: { x: number; y: number; barRect?: DOMRect }) => {
    if (isTodoEvent(event)) {
      const originalTodo = todos.find(t => t.id === event.todoId);
      if (originalTodo) {
        setEditingTodo(originalTodo);
        if (clickPosition) {
          setTodoFormPosition({ x: clickPosition.x, y: clickPosition.y });
        }
        setShowTodoForm(true);
        return;
      }
    }
    // URL-synced imported events are read-only (not file imports or Google Calendar)
    const importedUrl = (event as any).importedCalendarUrl;
    if (event.isImported && importedUrl && importedUrl !== 'file://imported' && !isGoogleCalendarUrl(importedUrl)) {
      setImportedDetailReadOnly(false);
      setImportedDetailEvent(event);
      setImportedDetailPosition(clickPosition || null);
      return;
    }
    // Viewer-role users see a read-only detail modal for all space events
    if (isViewer) {
      setImportedDetailReadOnly(true);
      setImportedDetailEvent(event);
      setImportedDetailPosition(clickPosition || null);
      return;
    }
    // On touch devices, show bottom sheet preview
    if (isTouchDevice) {
      setMobilePreviewEvent(event);
      return;
    }
    // Pre-select Google Calendar in the form when editing an imported Google Calendar event
    if (event.isImported && isGoogleCalendarUrl(importedUrl)) {
      setSelectedGoogleCalendarId(googleCalIdFromUrl(importedUrl) || 'primary');
    }
    // Child events in a recurring series don't carry the recurrence_rule themselves —
    // inherit it from the parent so the RecurrenceSelector shows the correct value.
    let eventToEdit = event;
    if (!event.recurrence_rule && event.parent_event_id) {
      const parent = events.find(e => e.id === event.parent_event_id);
      if (parent?.recurrence_rule) {
        eventToEdit = { ...event, recurrence_rule: parent.recurrence_rule };
      }
    }
    calendarState.openEventForEdit(eventToEdit, clickPosition);
  };

  // Only show loading screen on initial load, not during space transitions
  const isInitialLoading = !hasInitialDataRef.current && (loading || calendarsLoading || importedCalendarsLoading);
  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-[var(--surface-secondary)] flex flex-col items-center justify-center gap-4">
        <div className="w-8 h-8 border-2 border-[var(--border-primary)] border-t-[var(--accent-primary)] rounded-full" style={{ animation: 'spin 0.8s linear infinite' }} />
        <div className="text-[var(--content-tertiary)] text-sm">Loading your calendar…</div>
      </div>
    );
  }

  return (
    <CalendarProvider
      calendars={calendars}
      todos={todos}
      kanbanColumns={kanbanColumns}
      toggleTodoComplete={toggleTodoComplete}
      updateTodoDueDate={async (todoId, dueDate, dueTime) => {
        await updateTodo(todoId, { due_date: dueDate, due_time: dueTime ?? undefined });
      }}
      quickUpdateEvent={async (eventId, updates) => {
        // Todo event bars have synthetic IDs like "todo-{uuid}"
        if (typeof eventId === 'string' && eventId.startsWith('todo-')) {
          const todoId = eventId.slice(5);
          const newDate = updates.dates?.[0];
          if (newDate) await updateTodo(todoId, { due_date: newDate });
          return;
        }
        // Check if this is an imported event (Google Calendar or file import)
        const importedEvent = importedEvents.find(e => e.id === eventId);
        if (importedEvent) {
          await updateImportedEvent(eventId, updates as any);
          // If Google Calendar event, push change to Google
          const calendar = importedCalendars.find(c => c.id === importedEvent.calendar_id);
          if (isGoogleCalendarUrl(calendar?.url) && importedEvent.external_uid && !importedEvent.external_uid.startsWith('temp-')) {
            const merged = { ...importedEvent, ...updates };
            const targetCal = googleCalIdFromUrl(calendar!.url) || undefined;
            gcalSync.pushEvent({ ...merged, notes: merged.description } as any, targetCal, importedEvent.external_uid);
          }
        } else {
          await eventHandlers.quickUpdateEvent(eventId, updates);
        }
      }}
      deleteMultipleEvents={eventHandlers.deleteMultipleEvents}
    >
    <div data-calendar-frame className="h-[100dvh] bg-[var(--surface-secondary)] p-1.5 sm:p-3 transition-colors flex flex-row gap-1.5 sm:gap-2 overflow-hidden" style={{ paddingBottom: 'max(6px, env(safe-area-inset-bottom))', paddingLeft: 'max(6px, env(safe-area-inset-left))', paddingRight: 'max(6px, env(safe-area-inset-right))' }}>
      {/* Sidebar — hidden on mobile */}
        <div
          data-print-hide
          className={`hidden sm:block flex-shrink-0 h-full transition-all duration-300 ease-in-out ${calendarState.sidebarCollapsed ? '' : 'overflow-hidden'}`}
          style={{ width: calendarState.sidebarCollapsed ? '40px' : '224px' }}
        >
          <CalendarSidebar
            collapsed={calendarState.sidebarCollapsed}
            onToggleCollapse={() => calendarState.setSidebarCollapsed(!calendarState.sidebarCollapsed)}
            events={events}
            filteredEvents={filteredEvents}
            importedCalendars={importedCalendars}
            importedEvents={importedEvents}
            currentYear={calendarState.currentYear}
            todos={todos}
            kanbanColumns={kanbanColumns}
            onOpenTodo={(todoId) => {
              const todo = todos.find(t => t.id === todoId);
              if (!todo) return;
              setEditingTodo(todo);
              setShowTodoForm(true);
            }}
            onAddTodo={() => {
              setEditingTodo(null);
              setDefaultColumnId(kanbanColumns.find(c => !c.is_done_column)?.id);
              const t = new Date();
              setDefaultTodoDueDate(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`);
              setShowTodoForm(true);
            }}
            onQuickAddTodo={async (title) => {
              const col = kanbanColumns.find(c => !c.is_done_column);
              if (!col) return;
              const t = new Date();
              const today = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
              await addTodo({ column_id: col.id, title, due_date: today });
            }}
            selectedCalendarFilters={calendarState.selectedCalendarFilters}
            onToggleCalendarFilter={calendarState.toggleCalendarFilter}
            onSelectAllTags={() => {
              const unassigned = getUnassignedEvents(events, calendars);
              const tagIds = calendars.map(t => t.id);
              if (unassigned.length > 0) tagIds.push(UNASSIGNED_CALENDAR_ID);
              calendarState.setSelectedCalendarFilters(new Set(tagIds));
            }}
            onDeselectAllTags={() => calendarState.setSelectedCalendarFilters(new Set())}
            onToggleCalendar={toggleCalendar}
            onUpdateCalendar={updateImportedCalendar}
            onSyncCalendar={handleSyncCalendar}
            syncing={combinedSyncing}
            onOpenCategoryMenu={(category, anchor, isNew) => {
              setQuickMenuCategory(category);
              setQuickMenuCategoryAnchor(anchor);
              setIsNewCategory(isNew);
            }}
            onOpenCalendarMenu={(calendar, anchor) => {
              setQuickMenuCalendar(calendar);
              setQuickMenuAnchor(anchor);
            }}
            onShowAddCalendarModal={() => setShowAddCalendarModal(true)}
            onOpenSpaceSettings={() => calendarState.setShowSettingsPage(true)}
            onSignOut={signOut}
            searchQuery={calendarState.searchQuery}
            onSearchChange={calendarState.setSearchQuery}
            contentTags={spaceTags.tags}
            activeContentTagFilter={activeContentTagFilter}
            onContentTagFilterChange={setActiveContentTagFilter}
          />
        </div>

      {/* Right section: header + calendar (header is same width as calendar) */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        {/* Header (hidden when printing) */}
        <div data-print-hide>
        <CalendarHeader
          viewMode={calendarState.viewMode}
          currentYear={calendarState.currentYear}
          currentMonth={calendarState.currentMonth}
          currentWeekStart={calendarState.currentWeekStart}
          yearZoom={calendarState.yearZoom}
          yearLayout={yearLayout}
          onViewModeChange={(mode) => calendarState.setViewMode(mode)}
          onPrev={calendarState.handleNavPrev}
          onNext={calendarState.handleNavNext}
          onToday={calendarState.handleNavToday}
          onAddEvent={handleAddEventFromHeader}
          onYearZoomToggle={handleYearZoomToggle}
          onYearLayoutToggle={handleYearLayoutToggle}
          onAddTodo={handleAddTodoFromHeader}
          showTodosOnCalendar={showTodosOnCalendar}
          onShowTodosOnCalendarChange={(show) => {
            setShowTodosOnCalendar(show);
            localStorage.setItem('calendar-show-todos', String(show));
          }}
          onPrint={() => setShowPrintDialog(true)}
          onOpenDrawer={() => setMobileDrawerOpen(true)}
          onSetYear={(year) => {
            calendarState.setNavDirection(year > calendarState.currentYear ? 'next' : year < calendarState.currentYear ? 'prev' : null);
            calendarState.setCurrentYear(year);
          }}
          onSetMonth={(month) => {
            calendarState.setNavDirection(month > calendarState.currentMonth ? 'next' : month < calendarState.currentMonth ? 'prev' : null);
            calendarState.setCurrentMonth(month);
          }}
          onSetWeek={(date) => {
            const d = new Date(date);
            const day = d.getDay();
            const diff = d.getDate() - ((day + 6) % 7);
            d.setDate(diff);
            d.setHours(0, 0, 0, 0);
            calendarState.setNavDirection(d > calendarState.currentWeekStart ? 'next' : 'prev');
            calendarState.setCurrentWeekStart(d);
            calendarState.setCurrentYear(d.getFullYear());
            calendarState.setCurrentMonth(d.getMonth());
          }}
          onTaskSettings={() => boardSettingsRef.current?.()}
          taskSettingsHasChanges={taskSettingsHasChanges}
          onScheduleSettings={() => scheduleSettingsRef.current?.()}
          scheduleSettingsHasChanges={scheduleSettingsHasChanges}
        />
        </div>

        {/* Print title — hidden on screen, shown when printing (outside rounded frame) */}
        <div data-print-title className="hidden px-4 py-2 text-sm font-semibold text-[var(--content-primary)]">
          {printTitle}
        </div>

        {/* Main Content - Calendar */}
        <div className="flex-1 bg-[var(--calendar-bg)] rounded-2xl overflow-hidden flex flex-col border border-[var(--border-primary)] relative">

          <AnimatePresence mode="wait">
          <motion.div
            key={calendarState.viewMode === 'year' ? `year-${yearLayout}` : calendarState.viewMode}
            initial={{ opacity: 0, scale: 0.985 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.985 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="flex-1 flex flex-col overflow-hidden"
          >
          <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={calendarState.viewMode === 'week'
              ? `nav-${calendarState.currentWeekStart.toISOString()}`
              : `nav-${calendarState.currentYear}-${calendarState.currentMonth}`}
            initial={calendarState.navDirection ? { opacity: 0, x: calendarState.navDirection === 'next' ? 50 : -50 } : false}
            animate={{ opacity: 1, x: 0 }}
            exit={calendarState.navDirection ? { opacity: 0, x: calendarState.navDirection === 'next' ? -50 : 50, transition: { duration: 0.1 } } : { opacity: 0, transition: { duration: 0.08 } }}
            transition={{ type: 'spring', stiffness: 500, damping: 35 }}
            className="flex-1 flex flex-col overflow-hidden"
          >
          <Suspense fallback={null}>
          {calendarState.viewMode === 'schedule' ? (
            <ScheduleView
              events={filteredEvents}
              currentYear={calendarState.currentYear}
              selectedCalendarFilters={calendarState.selectedCalendarFilters}
              onToggleCalendarFilter={calendarState.toggleCalendarFilter}
              onClearTagFilters={() => calendarState.setSelectedCalendarFilters(new Set())}
              onEditEvent={handleEventClick}
              onCreateEvent={() => {
                const today = new Date();
                const dateKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                calendarState.openNewEventForm({
                  dateKey,
                  position: { x: window.innerWidth / 2, y: window.innerHeight / 2, centerHorizontally: true },
                });
              }}
              scrollToToday={calendarState.scrollToTodayCounter}
              showTodosOnCalendar={showTodosOnCalendar}
              onPrint={() => setShowPrintDialog(true)}
              settingsOpenRef={scheduleSettingsRef}
              onSettingsStateChange={setScheduleSettingsHasChanges}
              contentTagsByEventId={eventContentTagsMap}
            />
          ) : calendarState.viewMode === 'week' ? (
            <WeekView
              weekStart={calendarState.currentWeekStart}
              events={filteredEvents}
              onEventClick={handleEventClick}
              onCellClick={!canEdit ? readOnlyNotice : (dateKey, hour) => {
                calendarState.openNewEventForm({
                  dateKey,
                  position: { x: window.innerWidth / 2, y: window.innerHeight / 2, centerHorizontally: true },
                  allDay: false,
                  startTime: hour.toString().padStart(2, '0') + ':00',
                  endTime: ((hour + 1) % 24).toString().padStart(2, '0') + ':00',
                });
              }}
              onCreateEventRange={(startDateKey, endDateKey, startHour, startMinute, endHour, endMinute) => {
                calendarState.openNewEventForm({
                  dateKey: startDateKey,
                  endDateKey,
                  position: { x: window.innerWidth / 2, y: window.innerHeight / 2, centerHorizontally: true },
                  allDay: false,
                  startTime: `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`,
                  endTime: `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`,
                });
              }}
              showPastDaysGreyed={calendarState.showPastDaysGreyed}
              editingEventId={calendarState.editingEvent?.id}
              previewColor={calendarState.previewColor}
              previewTitle={calendarState.eventTitle}
              previewStartTime={calendarState.startTime}
              previewEndTime={calendarState.endTime}
              eventFormOpen={calendarState.showEventForm}
            />
          ) : calendarState.viewMode === 'todo' ? (
            <BoardView
              todos={tagFilteredTodoIds !== null ? todos.filter(t => tagFilteredTodoIds.has(String(t.id))) : todos}
              todoContentTagsMap={todoContentTagsMap}
              members={members}
              isPersonalSpace={isPersonalSpace}
              canEdit={canEdit}
              defaultColumnId={kanbanColumns[0]?.id || ''}
              onAddTodoFull={async (data) => {
                let tempId: string | null = null;
                const newTodo = await addTodo(
                  {
                    column_id: data.column_id || kanbanColumns[0]?.id || '',
                    title: data.title,
                    notes: data.notes,
                    due_date: data.due_date,
                    due_time: data.due_time,
                    duration: data.duration,
                    priority: data.priority,
                    assignee_id: data.assignee_id,
                  },
                  data.contentTags.length > 0
                    ? (id) => {
                        tempId = id;
                        // Register in pendingTagUpdatesRef so the fetchTodoTags re-fetch
                        // (triggered by todos state change) doesn't overwrite our optimistic entry
                        pendingTagUpdatesRef.current.set(id, data.contentTags);
                        setTodoContentTagsMap(prev => { const m = new Map(prev); m.set(id, data.contentTags); return m; });
                      }
                    : undefined,
                );
                if (newTodo && data.contentTags.length > 0) {
                  // Swap tempId → real ID in the pending guard and the map
                  if (tempId) pendingTagUpdatesRef.current.delete(tempId);
                  pendingTagUpdatesRef.current.set(newTodo.id, data.contentTags);
                  setTodoContentTagsMap(prev => {
                    const m = new Map(prev);
                    if (tempId) m.delete(tempId);
                    m.set(newTodo.id, data.contentTags);
                    return m;
                  });
                  await spaceTags.setTodoTags(newTodo.id, data.contentTags.map(t => t.id));
                  pendingTagUpdatesRef.current.delete(newTodo.id);
                }
              }}
              onUpdateTodo={updateTodo}
              onUpdateTodoContentTags={async (todoId, tagIds) => {
                const newTags = tagIds.map(id => spaceTags.tags.find(t => t.id === id)).filter(Boolean) as ContentTag[];
                pendingTagUpdatesRef.current.set(todoId, newTags);
                setTodoContentTagsMap(prev => { const m = new Map(prev); m.set(todoId, newTags); return m; });
                await spaceTags.setTodoTags(todoId, tagIds);
                pendingTagUpdatesRef.current.delete(todoId);
              }}
              onDeleteTodo={deleteTodo}
              onArchiveTodo={archiveTodo}
              onUnarchiveTodo={unarchiveTodo}
              onToggleTodoComplete={toggleTodoComplete}
              onEditTodo={(todo, position) => {
                setEditingTodo(todo);
                if (position) setTodoFormPosition(position);
                setShowTodoForm(true);
              }}
              contentTags={spaceTags.tags}
              onCreateContentTag={spaceTags.createTag}
              searchQuery={calendarState.searchQuery}
              settingsOpenRef={boardSettingsRef}
              onSettingsStateChange={setTaskSettingsHasChanges}
            />
          ) : calendarState.viewMode === 'year' && yearLayout === 'vertical' ? (
            <VerticalYearView
              currentYear={calendarState.currentYear}
              events={nonBackgroundEvents}
              showPastDaysGreyed={calendarState.showPastDaysGreyed}
              selectedCalendar={calendarState.selectedCalendar}
              editingEventId={calendarState.editingEvent?.id}
              previewColor={calendarState.previewColor}
              hoverCell={calendarState.hoverCell}
              onCellHover={calendarState.setHoverCell}
              onEventClick={handleEventClick}
              hoveredEventId={calendarState.hoveredEventId}
              onEventHover={calendarState.handleEventHover}
              onCreateEventRange={calendarState.handleCreateEventRange}
              onClearCreation={() => {}}
              eventFormOpen={calendarState.showEventForm}
              maxEventRows={calendarState.maxEventRows}
              backgroundColorMap={backgroundColorMap}
              expandedMonths={calendarState.expandedMonths}
              onMonthExpand={calendarState.toggleMonthExpansion}
              scrollToToday={calendarState.scrollToTodayCounter}
              yearZoom={calendarState.yearZoom}
              onCellTap={(dateKey, cellRect) => {
                calendarState.openNewEventForm({
                  dateKey,
                  position: cellRect ? {
                    x: cellRect.left + cellRect.width / 2,
                    y: cellRect.top + cellRect.height / 2,
                    barRect: cellRect,
                  } : undefined,
                });
              }}
            />
          ) : (
            <CalendarGrid
              viewMode={calendarState.viewMode}
              currentYear={calendarState.currentYear}
              currentMonth={calendarState.currentMonth}
              events={nonBackgroundEvents}
              backgroundColorMap={backgroundColorMap}
              selectedCells={calendarState.selectedCells}
              expandedMonths={calendarState.expandedMonths}
              selectedCalendar={calendarState.selectedCalendar}
              editingEventId={calendarState.editingEvent?.id}
              previewColor={calendarState.previewColor}
              onCellClick={canEdit ? calendarState.handleCellClick : readOnlyNotice}
              onCreateEventRange={calendarState.handleCreateEventRange}
              onMonthExpand={calendarState.toggleMonthExpansion}
              showPastDaysGreyed={calendarState.showPastDaysGreyed}
              maxEventRows={calendarState.maxEventRows}
              hoverCell={calendarState.hoverCell}
              onCellHover={calendarState.setHoverCell}
              onEventClick={handleEventClick}
              hoveredEventId={calendarState.hoveredEventId}
              onEventHover={calendarState.handleEventHover}
              onClearCreation={() => {}}
              eventFormOpen={calendarState.showEventForm}
              yearZoom={calendarState.yearZoom}
              onCellTap={(dateKey, cellRect) => {
                calendarState.openNewEventForm({
                  dateKey,
                  position: cellRect ? {
                    x: cellRect.left + cellRect.width / 2,
                    y: cellRect.top + cellRect.height / 2,
                    barRect: cellRect,
                  } : undefined,
                });
              }}
            />
          )}
          </Suspense>
          </motion.div>
          </AnimatePresence>
          </motion.div>
          </AnimatePresence>


        </div>

        {/* Print tag legend — hidden on screen, shown when printing (outside rounded frame) */}
        <div data-print-legend className="hidden">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2">
            {calendars.map(tag => (
              <div key={tag.id} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${tag.color}`} />
                <span className="text-[10px] text-[var(--content-secondary)]">{tag.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Event Tooltip (desktop only) */}
        {!isTouchDevice && (
          <EventTooltip
            event={calendarState.hoveredEvent}
            position={calendarState.tooltipPosition}
          />
        )}

        {/* Mobile Event Preview Sheet */}
        {isTouchDevice && (
          <MobileEventSheet
            event={mobilePreviewEvent}
            calendars={calendars}
            onEdit={(event) => {
              setMobilePreviewEvent(null);
              // Pre-select Google Calendar when editing a Google Calendar event
              const importedUrl = (event as any).importedCalendarUrl;
              if (event.isImported && isGoogleCalendarUrl(importedUrl)) {
                setSelectedGoogleCalendarId(googleCalIdFromUrl(importedUrl) || 'primary');
              }
              let eventToEdit = event;
              if (!event.recurrence_rule && event.parent_event_id) {
                const parent = events.find(e => e.id === event.parent_event_id);
                if (parent?.recurrence_rule) eventToEdit = { ...event, recurrence_rule: parent.recurrence_rule };
              }
              calendarState.openEventForEdit(eventToEdit);
            }}
            onDelete={(event) => {
              setMobilePreviewEvent(null);
              if (event.recurrence_rule || event.parent_event_id) {
                calendarState.setEventToDelete(event);
                calendarState.setShowDeleteDialog(true);
              } else if (event.isImported) {
                deleteImportedEvent(event.id);
                const externalUid = (event as any).externalUid;
                const importedUrl = (event as any).importedCalendarUrl;
                if (isGoogleCalendarUrl(importedUrl) && externalUid && !externalUid.startsWith('temp-')) {
                  gcalSync.deleteGoogleEventByGoogleId(externalUid, googleCalIdFromUrl(importedUrl) || undefined);
                }
              } else {
                const snapshot = { ...event };
                eventHandlers.deleteEvent(event.id);
                toast('Event deleted', 'info', {
                  label: 'Undo',
                  onClick: () => saveEvent(snapshot),
                });
                if (event.space_id && !isPersonalSpace) {
                  notifySpaceMembers({
                    type: 'event_deleted',
                    spaceId: event.space_id,
                    eventTitle: event.title,
                    actorId: user!.id,
                    actorName: user!.user_metadata?.display_name || user!.email || 'Someone',
                  });
                }
              }
            }}
            onClose={() => setMobilePreviewEvent(null)}
          />
        )}

        {/* Read-only detail for URL-synced imported events and viewer-role space events */}
        <ImportedEventDetail
          isOpen={!!importedDetailEvent}
          event={importedDetailEvent}
          position={importedDetailPosition}
          readOnly={importedDetailReadOnly}
          calendarName={
            importedDetailReadOnly
              ? currentSpace?.name
              : importedDetailEvent
                ? importedCalendars.find(c => c.id === importedEvents.find(e => e.id === importedDetailEvent.id)?.calendar_id)?.name
                : undefined
          }
          calendarColor={
            importedDetailReadOnly
              ? undefined
              : importedDetailEvent
                ? importedCalendars.find(c => c.id === importedEvents.find(e => e.id === importedDetailEvent.id)?.calendar_id)?.color
                : undefined
          }
          onDelete={() => {
            if (importedDetailEvent) {
              deleteImportedEvent(importedDetailEvent.id);
            }
            setImportedDetailEvent(null);
          }}
          onClose={() => { setImportedDetailEvent(null); setImportedDetailReadOnly(false); }}
        />

        {/* Event Form Modal */}
        <EventFormModal
          readOnly={!canEdit}
          isOpen={calendarState.showEventForm}
          position={calendarState.eventFormPosition}
          formSource={eventFormSource}
          eventTitle={calendarState.eventTitle}
          eventNotes={calendarState.eventNotes}
          startDate={calendarState.startDate}
          endDate={calendarState.endDate}
          selectedCalendar={calendarState.selectedCalendar}
          allDay={calendarState.allDay}
          fullSize={calendarState.fullSize}
          startTime={calendarState.startTime}
          endTime={calendarState.endTime}
          recurrenceRule={calendarState.recurrenceRule}
          editingEvent={calendarState.editingEvent}
          onTitleChange={calendarState.setEventTitle}
          onNotesChange={calendarState.setEventNotes}
          onStartDateChange={calendarState.setStartDate}
          onEndDateChange={calendarState.setEndDate}
          onCalendarChange={calendarState.setSelectedCalendar}
          onAllDayChange={calendarState.setAllDay}
          onFullSizeChange={calendarState.setFullSize}
          onStartTimeChange={calendarState.handleStartTimeChange}
          onEndTimeChange={calendarState.setEndTime}
          onRecurrenceChange={calendarState.setRecurrenceRule}
          onSelectedCellsChange={calendarState.setSelectedCells}
          onPreviewColorChange={calendarState.setPreviewColor}
          googleCalendarConnected={gcalSync.connected}
          googleCalendarColor={importedCalendars.find(c => c.url === 'google-calendar-sync' || c.url.startsWith('google-calendar:'))?.color || 'bg-blue-500'}
          selectedGoogleCalendarId={selectedGoogleCalendarId}
          onGoogleCalendarChange={setSelectedGoogleCalendarId}
          contentTags={spaceTags.tags}
          selectedContentTags={selectedContentTags}
          onContentTagsChange={setSelectedContentTags}
          onCreateContentTag={spaceTags.createTag}
          restoreFromDraftOnOpen={openEventFromDraft}
          onSave={handleSaveEvent}
          onDelete={handleDeleteFromForm}
          onClose={() => { setSelectedGoogleCalendarId(null); setOpenEventFromDraft(false); calendarState.resetForm(); }}
          kanbanColumns={kanbanColumns}
          onCreateTodo={async (data) => {
            await addTodo({
              column_id: data.column_id,
              title: data.title,
              notes: data.notes,
              due_date: data.due_date,
              due_time: data.due_time,
              calendar_id: data.calendar_id,
              priority: data.priority,
            });
            calendarState.resetForm();
          }}
        />

        {/* Todo Form Modal */}
        <TodoFormModal
          isOpen={showTodoForm}
          todo={editingTodo}
          members={members}
          isPersonalSpace={isPersonalSpace}
          defaultColumnId={defaultColumnId}
          defaultDueDate={defaultTodoDueDate}
          defaultTitle={defaultTodoTitle}
          position={todoFormPosition}
          restoreFromDraftOnOpen={openTaskFromDraft}
          contentTags={spaceTags.tags}
          selectedContentTags={selectedTodoContentTags}
          onContentTagsChange={setSelectedTodoContentTags}
          onCreateContentTag={spaceTags.createTag}
          onSave={async (todoData) => {
            const tagsToSave = [...selectedTodoContentTags];
            if (editingTodo) {
              const todoId = editingTodo.id;
              pendingTagUpdatesRef.current.set(todoId, tagsToSave);
              setTodoContentTagsMap(prev => { const m = new Map(prev); m.set(todoId, tagsToSave); return m; });
              await Promise.all([
                updateTodo(todoId, todoData),
                spaceTags.setTodoTags(todoId, tagsToSave.map(t => t.id)),
              ]);
              pendingTagUpdatesRef.current.delete(todoId);
            } else {
              const newTodo = await addTodo({
                column_id: todoData.column_id,
                title: todoData.title,
                notes: todoData.notes,
                due_date: todoData.due_date,
                due_time: todoData.due_time,
                duration: todoData.duration,
                calendar_id: todoData.calendar_id,
                assignee_id: todoData.assignee_id,
                priority: todoData.priority,
                reminder_minutes_before: todoData.reminder_minutes_before,
                reminder_fire_at: todoData.reminder_fire_at,
                reminder_sent_at: null,
              });
              if (newTodo) {
                pendingTagUpdatesRef.current.set(newTodo.id, tagsToSave);
                setTodoContentTagsMap(prev => { const m = new Map(prev); m.set(newTodo.id, tagsToSave); return m; });
                await spaceTags.setTodoTags(newTodo.id, tagsToSave.map(t => t.id));
                pendingTagUpdatesRef.current.delete(newTodo.id);
              }
            }
          }}
          onToggleComplete={editingTodo ? async () => { await toggleTodoComplete(editingTodo.id); } : undefined}
          onDelete={async () => {
            if (editingTodo) {
              await deleteTodo(editingTodo.id);
              setShowTodoForm(false);
              setEditingTodo(null);
            }
          }}
          onArchive={async () => {
            if (editingTodo) {
              await archiveTodo(editingTodo.id);
              setShowTodoForm(false);
              setEditingTodo(null);
            }
          }}
          onUnarchive={async () => {
            if (editingTodo) {
              await unarchiveTodo(editingTodo.id);
              setShowTodoForm(false);
              setEditingTodo(null);
            }
          }}
          onClose={() => {
            setShowTodoForm(false);
            setEditingTodo(null);
            setDefaultColumnId(undefined);
            setTodoFormPosition(null);
            setOpenTaskFromDraft(false);
          }}
        />

        {/* Tag Manager Modal */}
        {calendarState.showTagManager && (
          <TagManagerModal
            calendars={calendars}
            events={events}
            onAddTag={addCalendar}
            onUpdateTag={updateCalendar}
            onDeleteTag={deleteCalendar}
            onClose={() => calendarState.setShowTagManager(false)}
            onEventUpdate={modifyEvent}
            refetch={refetch}
            selectedCalendar={calendarState.selectedCalendar}
            setSelectedCalendar={calendarState.setSelectedCalendar}
          />
        )}

        {/* Settings Modal */}
        {calendarState.showSettingsMenu && (
          <SettingsModal
            theme={theme}
            toggleTheme={toggleTheme}
            showPastDaysGreyed={calendarState.showPastDaysGreyed}
            setShowPastDaysGreyed={calendarState.setShowPastDaysGreyed}
            onOpenTagManager={() => {
              calendarState.setShowTagManager(true);
              calendarState.setShowSettingsMenu(false);
            }}
            onClose={() => calendarState.setShowSettingsMenu(false)}
            events={events}
            onImport={async (importedEvents) => {
              for (const event of importedEvents) {
                await saveEvent(event as Omit<CalendarEvent, 'id'>);
              }
              await refetch();
            }}
          />
        )}

        {/* Delete Recurring Event Dialog */}
        <DeleteRecurringDialog
          isOpen={calendarState.showDeleteDialog}
          onClose={() => {
            calendarState.setShowDeleteDialog(false);
            calendarState.setEventToDelete(null);
          }}
          onDeleteSingle={async () => {
            if (calendarState.eventToDelete) {
              const snapshot = { ...calendarState.eventToDelete };
              await eventHandlers.deleteEvent(snapshot.id);
              calendarState.setShowDeleteDialog(false);
              calendarState.setEventToDelete(null);
              calendarState.resetForm();
              toast('Event deleted', 'info', {
                label: 'Undo',
                onClick: () => saveEvent(snapshot),
              });
              if (snapshot.space_id && !isPersonalSpace) {
                notifySpaceMembers({
                  type: 'event_deleted',
                  spaceId: snapshot.space_id,
                  eventTitle: snapshot.title,
                  actorId: user!.id,
                  actorName: user!.user_metadata?.display_name || user!.email || 'Someone',
                });
              }
            }
          }}
          onDeleteSeries={async () => {
            if (calendarState.eventToDelete) {
              const event = calendarState.eventToDelete;
              await eventHandlers.deleteRecurringSeries(event);
              calendarState.setShowDeleteDialog(false);
              calendarState.setEventToDelete(null);
              calendarState.resetForm();
              toast('Series deleted', 'info');
              if (event.space_id && !isPersonalSpace) {
                notifySpaceMembers({
                  type: 'event_deleted',
                  spaceId: event.space_id,
                  eventTitle: event.title,
                  actorId: user!.id,
                  actorName: user!.user_metadata?.display_name || user!.email || 'Someone',
                });
              }
            }
          }}
          eventTitle={calendarState.eventToDelete?.title || ''}
        />

        {/* Edit Recurring Event Dialog */}
        <EditRecurringDialog
          isOpen={showEditRecurringDialog}
          eventTitle={pendingRecurringEditRef.current?.editingEvent.title || ''}
          onClose={() => {
            setShowEditRecurringDialog(false);
            pendingRecurringEditRef.current = null;
          }}
          onEditSingle={async () => {
            const pending = pendingRecurringEditRef.current;
            if (!pending) return;
            setShowEditRecurringDialog(false);
            pendingRecurringEditRef.current = null;
            const { editingEvent, formData } = pending;
            calendarState.resetForm();
            savingRef.current = true;
            try {
              const ok = await eventHandlers.updateEvent(editingEvent, formData);
              if (ok) {
                toast('Event saved', 'success');
              } else {
                toast('Failed to save event', 'error');
              }
            } finally {
              savingRef.current = false;
            }
          }}
          onEditSeries={async () => {
            const pending = pendingRecurringEditRef.current;
            if (!pending) return;
            setShowEditRecurringDialog(false);
            pendingRecurringEditRef.current = null;
            const { editingEvent, formData } = pending;
            calendarState.resetForm();
            savingRef.current = true;
            try {
              const ok = await eventHandlers.updateEventSeries(editingEvent, formData);
              if (ok) {
                toast('All events updated', 'success');
              } else {
                toast('Failed to update series', 'error');
              }
            } finally {
              savingRef.current = false;
            }
          }}
        />

        {/* Add Calendar Modal */}
        <AddCalendarModal
          isOpen={showAddCalendarModal}
          onClose={() => setShowAddCalendarModal(false)}
          onAdd={addImportedCalendar}
          onImportFile={importFromFile}
          googleCalendarConnected={gcalSync.connected}
          onConnectGoogle={signInWithGoogle}
        />

        {/* Calendar Quick Menu */}
        {quickMenuCalendar && quickMenuAnchor && (
          <CalendarQuickMenu
            isOpen={true}
            onClose={() => {
              setQuickMenuCalendar(null);
              setQuickMenuAnchor(null);
            }}
            calendar={quickMenuCalendar}
            onUpdate={updateImportedCalendar}
            onDelete={deleteImportedCalendar}
            onSync={handleSyncCalendar}
            syncing={combinedSyncing === quickMenuCalendar.id}
            anchorElement={quickMenuAnchor}
          />
        )}

        {/* Category Quick Menu */}
        {quickMenuCategory && quickMenuCategoryAnchor && (
          <CategoryQuickMenu
            isOpen={true}
            onClose={() => {
              setQuickMenuCategory(null);
              setQuickMenuCategoryAnchor(null);
              setIsNewCategory(false);
            }}
            category={quickMenuCategory}
            isNew={isNewCategory}
            onUpdate={async (id, updates) => {
              await updateCalendar(id, updates);
            }}
            onCreate={async (data) => {
              await addCalendar({
                name: data.name,
                color: data.color,
              });
            }}
            onDelete={deleteCalendar}
            anchorElement={quickMenuCategoryAnchor}
            categoryEvents={
              isNewCategory ? [] : events.filter(e => {
                const tag = resolveCalendar(e, calendars);
                return tag?.id === quickMenuCategory.id;
              }).map(e => ({
                id: e.id,
                title: e.title,
                dates: e.dates,
                notes: e.notes,
                all_day: e.all_day,
                start_time: e.start_time,
                end_time: e.end_time
              }))
            }
            onNavigateToYear={(year) => calendarState.setCurrentYear(year)}
            userId={user?.id}
          />
        )}

        {/* Global Search */}
        <GlobalSearch
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          events={events}
          todos={todos}
          calendars={calendars}
          spaces={spaces}
          currentSpace={currentSpace}
          currentView={calendarState.viewMode}
          onNavigateToDate={handleSearchNavigateToDate}
          onOpenEvent={(event) => { calendarState.openEventForEdit(event); }}
          onOpenTodo={(todo) => { setEditingTodo(todo); setShowTodoForm(true); }}
          onSwitchSpace={setCurrentSpace}
          onNewEvent={handleSearchNewEvent}
          onNewTask={handleSearchNewTask}
          onSwitchView={(view) => calendarState.setViewMode(view)}
        />

        {/* Print Dialog */}
        {showPrintDialog && (
          <PrintDialog
            viewMode={calendarState.viewMode}
            currentYear={calendarState.currentYear}
            currentMonth={calendarState.currentMonth}
            currentWeekStart={calendarState.currentWeekStart}
            onClose={() => setShowPrintDialog(false)}
          />
        )}

        {/* Settings Page */}
        {calendarState.showSettingsPage && (
          <SettingsPage
            initialSection={settingsInitialSection}
            onClose={() => { calendarState.setShowSettingsPage(false); setSettingsInitialSection(undefined); }}
            onOpenShare={() => {
              calendarState.setShowSettingsPage(false);
              calendarState.setShowSettingsMenu(true);
            }}
            onOpenImportExport={() => {
              calendarState.setShowSettingsPage(false);
              calendarState.setShowSettingsMenu(true);
            }}
            showPastDaysGreyed={calendarState.showPastDaysGreyed}
            onShowPastDaysGreyedChange={calendarState.setShowPastDaysGreyed}
            maxEventRows={calendarState.maxEventRows}
            onMaxEventRowsChange={(value) => {
              calendarState.setMaxEventRows(value);
              localStorage.setItem('calendar-max-event-rows', value.toString());
            }}
            autoArchiveEnabled={taskSettings.auto_archive_enabled}
            autoArchiveDays={taskSettings.auto_archive_days}
            onAutoArchiveEnabledChange={(enabled) => updateTaskSettings({ auto_archive_enabled: enabled })}
            onAutoArchiveDaysChange={(days) => updateTaskSettings({ auto_archive_days: days })}
            calendars={calendars}
            events={events}
            onAddTag={addCalendar}
            onUpdateTag={updateCalendar}
            onDeleteTag={deleteCalendar}
            onEventUpdate={modifyEvent}
            refetchTags={refetch}
            reorderCalendars={reorderCalendars}
            selectedCalendar={calendarState.selectedCalendar}
            setSelectedCalendar={calendarState.setSelectedCalendar}
            showTodosOnCalendar={showTodosOnCalendar}
            onShowTodosOnCalendarChange={(show) => {
              setShowTodosOnCalendar(show);
              localStorage.setItem('calendar-show-todos', String(show));
            }}
            textTags={spaceTags.tags}
            textTagsLoading={spaceTags.loading}
            onCreateTextTag={spaceTags.createTag}
            onDeleteTextTag={spaceTags.deleteTag}
            onRenameTextTag={spaceTags.renameTag}
          />
        )}
      </div>

      {/* Mobile FAB — create event/task only */}
      {isTouchDevice && (
        <MobileBottomBar
          onAdd={() => {
            if (calendarState.viewMode === 'todo') {
              handleAddTodoFromHeader({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
            } else {
              const d = new Date();
              const todayKey = getDateKey(d.getFullYear(), d.getMonth(), d.getDate());
              handleAddEventFromHeader(todayKey, { x: window.innerWidth / 2, y: window.innerHeight / 2 });
            }
          }}
          viewMode={calendarState.viewMode}
        />
      )}

      {/* Mobile Drawer */}
      <MobileDrawer
        isOpen={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        viewMode={calendarState.viewMode}
        onViewModeChange={calendarState.setViewMode}
        onOpenSettings={() => calendarState.setShowSettingsPage(true)}
        searchQuery={calendarState.searchQuery}
        onSearchChange={calendarState.setSearchQuery}
        calendars={calendars}
        selectedCalendarFilters={calendarState.selectedCalendarFilters}
        onToggleCalendarFilter={calendarState.toggleCalendarFilter}
        onSelectAllTags={() => {
          const allSelected = calendars.length > 0 && calendars.every(t => calendarState.selectedCalendarFilters.has(t.id));
          if (allSelected) {
            calendarState.setSelectedCalendarFilters(new Set());
          } else {
            calendarState.setSelectedCalendarFilters(new Set(calendars.map(t => t.id)));
          }
        }}
        onOpenCategoryMenu={(tag, anchor, isNew) => {
          setQuickMenuCategory(tag);
          setQuickMenuCategoryAnchor(anchor as HTMLButtonElement);
          setIsNewCategory(isNew);
        }}
        importedCalendars={importedCalendars.map(c => ({ id: c.id, name: c.name, url: c.url, color: c.color, enabled: c.enabled }))}
        onToggleCalendar={toggleCalendar}
        onShowAddCalendarModal={() => setShowAddCalendarModal(true)}
        onSyncCalendar={handleSyncCalendar}
        syncing={combinedSyncing}
        onOpenCalendarMenu={(cal, anchor) => {
          setQuickMenuCalendar(cal);
          setQuickMenuAnchor(anchor as HTMLButtonElement);
        }}
        onSignOut={signOut}
      />

      {/* Draft cards — shown when an event or task draft was dismissed without saving */}
      <DraftCard
        onOpenEventDraft={handleOpenEventDraft}
        onOpenTaskDraft={handleOpenTaskDraft}
      />
    </div>
    </CalendarProvider>
  );
}

